import React, { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2 } from 'lucide-react';

function LoginHero() {
  return (
    <div className="hidden lg:flex lg:w-1/2 relative">
      <img
        src="https://images.unsplash.com/photo-1619976553860-b7ffbe9a093b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHx3b29kJTIwdGV4dHVyZXxlbnwwfHx8fDE3NzU5NDM2ODF8MA&ixlib=rb-4.1.0&q=85"
        alt="Wood texture" className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-[#1A1D21]/80" />
      <div className="relative z-10 flex flex-col items-center justify-center p-12 w-full">
        <img
          src="https://customer-assets.emergentagent.com/job_project-builder-215/artifacts/n5lbvytv_IMG_9406-no-bg%20v2%28RESIZE%29.png"
          alt="Roebuck Woodworks"
          className="w-72 md:w-96 object-contain"
        />
      </div>
    </div>
  );
}

function SubmitLabel({ loading, isLogin }) {
  if (loading) return <Loader2 className="w-5 h-5 animate-spin" />;
  if (isLogin) return 'Sign In';
  return 'Create Account';
}

function AuthForm({ isLogin, onSubmit, loading, error }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ email, password, name });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {!isLogin && (
        <div className="space-y-2">
          <Label htmlFor="name" className="text-[#F0EDE8] font-medium">Full Name</Label>
          <Input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="John Smith" required={!isLogin}
            className="bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl"
            data-testid="register-name-input" />
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor="email" className="text-[#F0EDE8] font-medium">Email</Label>
        <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com" required
          className="bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl"
          data-testid="login-email-input" />
      </div>
      <div className="space-y-2">
        <Label htmlFor="password" className="text-[#F0EDE8] font-medium">Password</Label>
        <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••" required
          className="bg-[#1A1D21] border-[#33383F] text-[#F0EDE8] placeholder:text-[#555D68] focus:border-[#4A9D6E] focus:ring-1 focus:ring-[#4A9D6E] rounded-xl"
          data-testid="login-password-input" />
      </div>
      {error && (
        <div className="bg-[#E55B5B]/10 border border-[#E55B5B]/30 text-[#E55B5B] px-4 py-3 rounded-xl text-sm" data-testid="login-error">
          {error}
        </div>
      )}
      <Button type="submit" disabled={loading} className="w-full bg-[#4A9D6E] hover:bg-[#3D8A5E] text-white rounded-xl h-11"
        data-testid="login-submit-button">
        <SubmitLabel loading={loading} isLogin={isLogin} />
      </Button>
    </form>
  );
}

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = useCallback(async ({ email, password, name }) => {
    setError('');
    setLoading(true);
    const result = isLogin
      ? await login(email, password)
      : await register(email, password, name);
    setLoading(false);
    if (result.success) {
      navigate('/');
    } else {
      setError(result.error);
    }
  }, [isLogin, login, register, navigate]);

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      <LoginHero />
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-[#1A1D21]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-6 flex justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_project-builder-215/artifacts/n5lbvytv_IMG_9406-no-bg%20v2%28RESIZE%29.png"
              alt="Roebuck Woodworks"
              className="w-48 object-contain"
            />
          </div>
          <div className="bg-[#23272E] border border-[#33383F] rounded-2xl p-8 shadow-lg">
            <AuthForm isLogin={isLogin} onSubmit={handleSubmit} loading={loading} error={error} />
            <div className="mt-6 text-center">
              <button type="button" onClick={() => { setIsLogin(!isLogin); setError(''); }}
                className="text-[#D4855A] hover:text-[#C4754A] font-medium text-sm transition-colors" data-testid="toggle-auth-mode">
                {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
              </button>
            </div>
          </div>
          <p className="mt-4 text-center text-[11px] text-[#555D68]" data-testid="stale-cache-hint">
            Interface looking outdated? Try a hard refresh — <kbd className="px-1 py-0.5 bg-[#23272E] border border-[#33383F] rounded text-[10px]">Ctrl</kbd>+<kbd className="px-1 py-0.5 bg-[#23272E] border border-[#33383F] rounded text-[10px]">Shift</kbd>+<kbd className="px-1 py-0.5 bg-[#23272E] border border-[#33383F] rounded text-[10px]">R</kbd> <span className="text-[#555D68]/70">(</span><kbd className="px-1 py-0.5 bg-[#23272E] border border-[#33383F] rounded text-[10px]">⌘</kbd>+<kbd className="px-1 py-0.5 bg-[#23272E] border border-[#33383F] rounded text-[10px]">Shift</kbd>+<kbd className="px-1 py-0.5 bg-[#23272E] border border-[#33383F] rounded text-[10px]">R</kbd> <span>on Mac)</span>
          </p>
        </div>
      </div>
    </div>
  );
}
